<script setup lang="ts">
import { useJournalOrder } from '../lib/useJournalOrder';
import JournalOrderSummary from './JournalOrderSummary.vue';
import JournalOrderForm from './JournalOrderForm.vue';
import JournalOrderSuccess from './JournalOrderSuccess.vue';

const { issue, isOpen, close } = useJournalOrder();

const isSent = ref(false);

const title = computed(() =>
  issue.value ? `Купить издание ${issue.value.title}` : 'Купить издание',
);

// Opening the modal on another issue always starts from an empty form
watch(issue, (value) => {
  if (value) {
    isSent.value = false;
  }
});
</script>

<template>
  <Modal :is-visible="isOpen" :title="title" hide-close @close="close">
    <div v-if="issue" :class="$style.modal">
      <!--
        The shared close button is pinned to the corner of the viewport at
        88×88 in white; the layout puts a small dark one inside the modal
      -->
      <button
        type="button"
        :class="$style.close"
        aria-label="Закрыть"
        @click="close"
      >
        <Icon name="my-icon:i-close" :class="$style.closeIcon" />
      </button>

      <h2 :class="$style.title">{{ title }}</h2>

      <JournalOrderSummary :issue="issue" :class="$style.summary" />

      <div :class="$style.body">
        <JournalOrderSuccess
          v-if="isSent"
          :title="issue.title"
          :class="$style.state"
        />
        <JournalOrderForm
          v-else
          :issue="issue"
          :class="$style.state"
          @sent="isSent = true"
        />
      </div>
    </div>
  </Modal>
</template>

<style module lang="scss">
/*
 * Areas rather than source order: the two columns of the desktop layout become
 * three bands on a phone, where the cover and the price stand between the
 * heading and the fields
 */
.modal {
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 340px;
  grid-template-areas:
    'title summary'
    'body summary';
  align-content: start;
  width: min(1080px, 90vw);
  max-height: 90vh;
  margin: 0 auto;
  overflow-y: auto;
  background-color: var(--color-white);
}

.title {
  @include text_h4;
  grid-area: title;
  color: var(--color-black-1);
  // The close button lives over the summary column, clear of the heading
  margin: 0;
  padding: var(--space-5x) var(--space-5x) var(--space-3x);
}

.summary {
  grid-area: summary;
}

.body {
  grid-area: body;
  display: flex;
  padding: 0 var(--space-5x) var(--space-5x);
}

.state {
  width: 100%;
}

.close {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
}

.closeIcon {
  width: 24px;
  height: 24px;
}

@media (max-width: 899px) {
  .modal {
    grid-template-columns: minmax(0, 1fr);
    grid-template-areas:
      'title'
      'summary'
      'body';
    width: 100%;
    max-height: 100%;
    height: 100%;
  }

  .title {
    @include text_h4_mobile;
    // Clear of the close button, which keeps its corner
    padding: var(--space-2-5x) var(--space-7x) var(--space-2-5x)
      var(--space-2-5x);
  }

  .body {
    padding: var(--space-3x) var(--space-2-5x) var(--space-4x);
  }
}
</style>
